import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  CircularProgress,
  Fade,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Store as StoreIcon,
  ExpandMore,
  Check,
  SignalCellular4Bar,
  SignalCellular2Bar,
  SignalCellularConnectedNoInternet0Bar,
  LocationOn,
  Phone,
  AccessTime,
  Sync,
  ChevronRight,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { usePharmacyConnection } from '../../services/pharmacyConnectionService';

export const PharmacyContextCard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [recentPharmacies, setRecentPharmacies] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    pharmacies,
    activePharmacy,
    loading,
    fetchPharmacies,
    setActivePharmacy,
  } = usePharmacyStore();

  const { connectionStatus, refresh: refreshConnection } = usePharmacyConnection(activePharmacy?.id);

  useEffect(() => {
    // Load recent pharmacies from localStorage
    const recent = localStorage.getItem('recentPharmacies');
    if (recent) {
      setRecentPharmacies(JSON.parse(recent));
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Fetch pharmacies if not already loaded
    if (pharmacies.length === 0) {
      fetchPharmacies();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery(''); // Clear search when closing
  };

  const handlePharmacyChange = async (pharmacyId: string) => {
    if (pharmacyId !== activePharmacy?.id) {
      await setActivePharmacy(pharmacyId);

      // Update recent pharmacies
      const updatedRecent = [pharmacyId, ...recentPharmacies.filter(id => id !== pharmacyId)].slice(0, 3);
      setRecentPharmacies(updatedRecent);
      localStorage.setItem('recentPharmacies', JSON.stringify(updatedRecent));

      // After switching pharmacy, land the user on the Operations Dashboard
      // (the per-pharmacy home) so they don't stay on a now-stale, other-
      // pharmacy screen.
      navigate('/order-fulfillment/operations-dashboard');
    }
    handleClose();
  };

  const getConnectionIcon = () => {
    switch (connectionStatus?.status) {
      case 'connected':
        return <SignalCellular4Bar sx={{ fontSize: 16, color: theme.palette.success.main }} />;
      case 'slow':
        return <SignalCellular2Bar sx={{ fontSize: 16, color: theme.palette.warning.main }} />;
      case 'disconnected':
        return <SignalCellularConnectedNoInternet0Bar sx={{ fontSize: 16, color: theme.palette.error.main }} />;
      case 'checking':
      default:
        return <Sync sx={{ fontSize: 16, color: theme.palette.info.main, animation: 'spin 1s linear infinite' }} />;
    }
  };

  const getConnectionColor = () => {
    switch (connectionStatus?.status) {
      case 'connected':
        return theme.palette.success.main;
      case 'slow':
        return theme.palette.warning.main;
      case 'disconnected':
        return theme.palette.error.main;
      case 'checking':
      default:
        return theme.palette.info.main;
    }
  };

  const open = Boolean(anchorEl);

  // Sort pharmacies alphabetically
  const sortedPharmacies = [...pharmacies].sort((a, b) => 
    a.name.localeCompare(b.name)
  );

  // Filter based on search
  const filteredPharmacies = sortedPharmacies.filter(pharmacy =>
    pharmacy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pharmacy.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pharmacy.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!activePharmacy && loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} sx={{ color: 'white' }} />
        <Typography variant="body2" sx={{ color: 'white' }}>
          Loading pharmacies...
        </Typography>
      </Box>
    );
  }

  // If no active pharmacy and not loading, show a prompt to select
  if (!activePharmacy && !loading) {
    return (
      <>
        <Tooltip title={isMobile ? 'Select Pharmacy' : ''}>
          <Box
            ref={anchorRef}
            onClick={handleClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1.5 },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.2),
              border: `2px dashed ${theme.palette.warning.main}`,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
                },
                '70%': {
                  boxShadow: '0 0 0 10px rgba(255, 193, 7, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(255, 193, 7, 0)',
                },
              },
              '&:hover': {
                bgcolor: alpha(theme.palette.warning.main, 0.3),
                transform: 'scale(1.02)',
              },
            }}
          >
            <StoreIcon sx={{ color: theme.palette.warning.main, fontSize: { xs: 20, sm: 24 } }} />
            {!isMobile && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.warning.main,
                  fontWeight: 600,
                }}
              >
                Select Pharmacy
              </Typography>
            )}
            <ExpandMore sx={{ color: theme.palette.warning.main, fontSize: { xs: 18, sm: 24 } }} />
          </Box>
        </Tooltip>

        {/* Still show the Popover for selection */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 380,
              maxWidth: 420,
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Select Pharmacy
              </Typography>
              <Tooltip title="Refresh pharmacies">
                <IconButton size="small" onClick={async () => {
                  await fetchPharmacies();
                }}>
                  <Sync />
                </IconButton>
              </Tooltip>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search pharmacies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredPharmacies.map((pharmacy) => (
                <ListItemButton
                  key={pharmacy.id}
                  onClick={() => handlePharmacyChange(pharmacy.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <ListItemIcon>
                    <StoreIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={pharmacy.name}
                    secondary={pharmacy.city ? `${pharmacy.city}, ${pharmacy.province}` : pharmacy.address}
                  />
                  {pharmacy.code && (
                    <Chip label={pharmacy.code} size="small" variant="outlined" />
                  )}
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Popover>
      </>
    );
  }

  // Get recent pharmacies (also sorted)
  const recentPharmacyList = recentPharmacies
    .map(id => pharmacies.find(p => p.id === id))
    .filter(Boolean)
    .sort((a, b) => a!.name.localeCompare(b!.name))
    .slice(0, 3);

  return (
    <>
      <Tooltip title={isMobile ? (activePharmacy?.name || 'Select Pharmacy') : ''}>
        <Box
          ref={anchorRef}
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 1.5 },
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.75, sm: 1 },
            borderRadius: 2,
            bgcolor: alpha(theme.palette.common.white, 0.1),
            cursor: 'pointer',
            transition: 'all 0.2s',
            maxWidth: { xs: 'auto', sm: 200, md: 260 },
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.2),
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <StoreIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getConnectionColor(),
                    border: '2px solid white',
                  }}
                />
              }
            />
          </Box>

          {/* Hide pharmacy name on mobile to save space */}
          {!isMobile && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { sm: 100, md: 160 },
                }}
              >
                {activePharmacy?.name || 'Select Pharmacy'}
              </Typography>
              {activePharmacy?.code && !isTablet && (
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.7),
                    display: 'block',
                    lineHeight: 1.2,
                  }}
                >
                  {activePharmacy.code} • {connectionStatus?.latency ? `${connectionStatus.latency}ms` : 'Checking...'}
                </Typography>
              )}
            </Box>
          )}

          <ExpandMore
            sx={{
              color: 'white',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              fontSize: { xs: 18, sm: 24 },
            }}
          />
        </Box>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: { xs: 300, sm: 380 },
            maxWidth: { xs: '90vw', sm: 420 },
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Select Pharmacy
            </Typography>
            <Tooltip title="Refresh connection">
              <IconButton size="small" onClick={async () => {
                await fetchPharmacies();
                if (activePharmacy?.id) {
                  await refreshConnection();
                }
              }}>
                <Sync fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {activePharmacy && (
            <Card
              variant="outlined"
              sx={{
                mb: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderColor: theme.palette.primary.main,
              }}
            >
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <StoreIcon sx={{ color: 'white' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {activePharmacy.name}
                    </Typography>
                    {activePharmacy.address && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {activePharmacy.address}, {activePharmacy.city}
                        </Typography>
                      </Box>
                    )}
                    {activePharmacy.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {activePharmacy.phone}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {getConnectionIcon()}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Last sync: {connectionStatus?.lastSync ? new Date(connectionStatus.lastSync).toLocaleTimeString() : 'Never'}
                  </Typography>
                </Box>
              </Box>
            </Card>
          )}

          {recentPharmacyList.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Recent Pharmacies
              </Typography>
              <List sx={{ mb: 2 }}>
                {recentPharmacyList.map((pharmacy) => (
                  <ListItemButton
                    key={pharmacy!.id}
                    onClick={() => handlePharmacyChange(pharmacy!.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: pharmacy!.id === activePharmacy?.id ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <StoreIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={pharmacy!.name}
                      secondary={pharmacy!.code}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {pharmacy!.id === activePharmacy?.id && (
                      <ListItemSecondaryAction>
                        <Check sx={{ color: theme.palette.primary.main }} />
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                ))}
              </List>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            All Pharmacies ({pharmacies.length})
          </Typography>
          
          {/* Search input - only show if more than 5 pharmacies */}
          {pharmacies.length > 5 && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, code, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {filteredPharmacies.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No pharmacies found matching "{searchQuery}"
                </Typography>
              </Box>
            ) : (
              filteredPharmacies.map((pharmacy) => (
              <ListItemButton
                key={pharmacy.id}
                onClick={() => handlePharmacyChange(pharmacy.id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: pharmacy.id === activePharmacy?.id ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <StoreIcon />
                </ListItemIcon>
                <ListItemText
                  primary={pharmacy.name}
                  secondary={
                    <>
                      <span>{pharmacy.code}</span>
                      {pharmacy.city && (
                        <>
                          <ChevronRight sx={{ fontSize: 12, verticalAlign: 'middle', mx: 0.5 }} />
                          <span>{pharmacy.city}</span>
                        </>
                      )}
                    </>
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
                {pharmacy.id === activePharmacy?.id && (
                  <ListItemSecondaryAction>
                    <Check sx={{ color: theme.palette.primary.main }} />
                  </ListItemSecondaryAction>
                )}
              </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};